import React, { useEffect, useState } from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { Grid, Box, Card, CardHeader, CardContent } from "@material-ui/core";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";

import httpClient from "../../lib/httpClient";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import {
  addExportButton,
  numberFilterOperators,
  getLatestUpdateDaily,
  tableCellRender,
  fromStringToNumberParser,
} from "../util/tableUtils";

const barColorMapping = () => ({
  0: {
    colorCode: "#cb6d67",
    description: i18n.t("kpiDashboard.geoAccuracy0"),
  },
  1: { colorCode: "#b1cb67", description: i18n.t("kpiDashboard.geoAccuracy1") },
  2: { colorCode: "#67cb95", description: i18n.t("kpiDashboard.geoAccuracy2") },
  3: { colorCode: "#FFA500", description: i18n.t("kpiDashboard.geoAccuracy3") },
  4: {
    colorCode: "#6789cb",
    description: i18n.t("kpiDashboard.geoAccuracy4"),
  },
  5: {
    colorCode: "#bd67cb",
    description: i18n.t("kpiDashboard.geoAccuracy5"),
  },
  6: {
    colorCode: "#d28ec9",
    description: i18n.t("kpiDashboard.geoAccuracy6"),
  },
});

export const GeoAccuracyKpi = () => {
  const { t } = useTranslation();

  const [geoDataFetching, setGeoDataFetching] = useState(false);
  const [geoData, setGeoData] = useState([]);
  const [plotData, setPlotData] = useState([]);
  const [lastUpdateDateGeoAccMsg, setLastUpdateDateGeoAccMsg] = useState("");

  useEffect(() => {
    setGeoDataFetching(true);
    getLatestUpdateDaily("/serviceLogs/upsertsFileDate?service=kpi_geolocation_accuracy").then(
      (lastUpdateMsg) => {
        setLastUpdateDateGeoAccMsg(lastUpdateMsg);
      },
    );
    httpClient()
      .get(`/kpi/geoaccuracy`)
      .then((res) => res.data)
      .then(async (data) => {
        const dataWithId = data.map((item, index) => ({ id: index, ...item }));
        setGeoData(dataWithId);
        setGeoDataFetching(false);
      });
  }, []);

  useEffect(() => {
    if (geoData.length) {
      const result = [];
      const years = [...new Set(geoData.map((item) => item.year))];

      years.forEach((year) => {
        geoData
          .filter((item) => item.year === year)
          .forEach((item, index) => {
            result.push({
              // in order to have the year label only once, not on every bar of the same year
              yearLabel: index === 2 ? year : "",
              loc_accuracy: item.loc_accuracy,
              count: parseInt(item.count, 10),
              year: item.year,
              percentage: item.percentage,
              color: barColorMapping()[parseInt(item.loc_accuracy)].colorCode,
            });
          });
      });
      setPlotData(result);
    }
  }, [geoData]);

  const geoAccuracyColumns = [
    {
      field: "year",
      headerName: t("kpiDashboard.year"),
    },
    { field: "loc_accuracy", headerName: t("kpiDashboard.accuracy") },
    { field: "count", headerName: t("kpiDashboard.count") },
    { field: "percentage", headerName: t("kpiDashboard.percentage") },
  ];

  const columns = geoAccuracyColumns.map((col) => ({
    ...col,
    valueParser: fromStringToNumberParser,
    filterOperators: numberFilterOperators,
    valueFormatter: (params) => (col.field !== "year" && tableCellRender(params.value)) || params.value,
    flex: 1,
    align: "right",
    headerAlign: "right",
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { count, loc_accuracy, year, percentage } = payload[0].payload;
      return (
        <div className="custom-tooltip" style={{ backgroundColor: "#fff", padding: "10px" }}>
          <p>{`${t("kpiDashboard.year")}: ${year}`}</p>
          <p>{`${t("kpiDashboard.accuracy")}: ${loc_accuracy}`}</p>
          <p>{`${t("kpiDashboard.count")}: ${count}`}</p>
          <p>{`${t("kpiDashboard.percentage")}: ${percentage}`}</p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = () => {
    return (
      <ul
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: 0,
        }}
      >
        {t("kpiDashboard.accuracy")}:
        <div style={{ margin: "4px" }}>
          {Object.keys(barColorMapping()).map((key, index) => (
            <li key={`item-${index}`} style={{ margin: "3px 5px", display: "flex", alignItems: "center" }}>
              <div
                style={{
                  minWidth: 20,
                  minHeight: 20,
                  backgroundColor: barColorMapping()[key].colorCode,
                  marginRight: 5,
                }}
              ></div>
              <span>
                {index}: {barColorMapping()[key].description}
              </span>
            </li>
          ))}
        </div>
      </ul>
    );
  };

  return (
    <Card>
      <CardHeader title={t("kpiDashboard.geolocationAccuracy")} />
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Box>
              <DataGridCystellarTable
                rows={geoData}
                columns={columns}
                getRowId={(rowData) => rowData.id}
                density="compact"
                loading={geoDataFetching}
                pagination={false}
                customToolbarComp={true}
                customToolbarWidgets={addExportButton("Accuratezza_Geolocalizzazione_Edifici")}
                customToolbarCompMsg={lastUpdateDateGeoAccMsg}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box p={1}>
              <ResponsiveContainer width="100%" height={500}>
                <BarChart
                  width={600}
                  height={400}
                  data={plotData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="yearLabel" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend content={<CustomLegend />} verticalAlign="bottom" />
                  <Bar dataKey="count" name={t("kpiDashboard.count")}>
                    {plotData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
